import { ZonedDateTime } from '@internationalized/date';
import { Button } from '../../../button/button';

const HistogramBarsUnloadedRegion = ({
  endTime,
  startTime,
  containerWidth,
  region,
  dragOffset,
  onLoadData,
  maxBarHeight,
  index,
}: {
  endTime: ZonedDateTime;
  startTime: ZonedDateTime;
  containerWidth: number;
  region: { start: ZonedDateTime; end: ZonedDateTime };
  dragOffset: number;
  onLoadData?: (startTime: ZonedDateTime, endTime: ZonedDateTime) => void;
  maxBarHeight: number;
  index: number;
}) => {
  const visibleDurationMs =
    endTime.toDate().getTime() - startTime.toDate().getTime();
  const pixelsPerMs = containerWidth / visibleDurationMs;

  // Calculate position in pixels relative to the original viewport
  const regionStartMs =
    region.start.toDate().getTime() - startTime.toDate().getTime();
  const regionEndMs =
    region.end.toDate().getTime() - startTime.toDate().getTime();

  const left = regionStartMs * pixelsPerMs + dragOffset;
  const width = (regionEndMs - regionStartMs) * pixelsPerMs;

  // Skip rendering if completely off-screen
  if (left + width < -100 || left > containerWidth + 100) {
    return null;
  }

  // Calculate button position with clamping behavior
  const buttonWidth = 100; // Approximate button width
  const buttonMargin = 16; // Margin from edges

  // The button wants to be in the center of the viewport
  const viewportCenter = containerWidth / 2;
  let buttonLeft = viewportCenter - buttonWidth / 2;

  // But it must stay within the unloaded region bounds
  const regionLeftBound = left + buttonMargin;
  const regionRightBound = left + width - buttonWidth - buttonMargin;

  // Clamp the button position to stay within the region
  buttonLeft = Math.max(
    regionLeftBound,
    Math.min(regionRightBound, buttonLeft),
  );

  // Only show button if the region is wide enough and at least partially visible
  const showButton =
    width > buttonWidth + 2 * buttonMargin &&
    left < containerWidth &&
    left + width > 0 &&
    onLoadData;

  return (
    <div key={`region-${index}`}>
      {/* Grey background */}
      <div
        className="absolute inset-y-0"
        style={{
          left: `${left}px`,
          width: `${width}px`,
          zIndex: 0,
          backgroundColor: document.documentElement.classList.contains('dark')
            ? '#374151'
            : '#f3f4f6',
          backgroundImage: `repeating-linear-gradient(
          45deg,
          transparent,
          transparent 10px,
          ${document.documentElement.classList.contains('dark') ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'} 10px,
          ${document.documentElement.classList.contains('dark') ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'} 20px
        )`,
        }}
      />

      {/* Load button */}
      {showButton && (
        <div
          className="absolute z-20"
          style={{
            left: `${buttonLeft}px`,
            top: `${maxBarHeight / 2 - 12}px`, // Center vertically
          }}
        >
          <Button
            variant="default"
            size="small"
            onClick={() => {
              const dragOffsetMs = -dragOffset / pixelsPerMs;
              const actualVisibleStart = startTime.add({
                milliseconds: dragOffsetMs,
              });
              const actualVisibleEnd = endTime.add({
                milliseconds: dragOffsetMs,
              });

              // Clamp the region to the visible range
              const clampedStart =
                region.start.compare(actualVisibleStart) > 0
                  ? region.start
                  : actualVisibleStart;
              const clampedEnd =
                region.end.compare(actualVisibleEnd) < 0
                  ? region.end
                  : actualVisibleEnd;

              onLoadData(clampedStart, clampedEnd);
            }}
            iconOnly
            leftIcon="lucide:plus"
            className="h-6 w-6 rounded-full"
          />
        </div>
      )}
    </div>
  );
};

export { HistogramBarsUnloadedRegion };
